import { z } from 'zod';
import { autLines } from './codevAutGrammar.js';
import { CODEV_SPECTRAL_RESPONSES } from './codevSpectralResponses.js';

export const CODEV_BATCH_VERSION = 'codev-batch/1' as const;
const index = '[1-9][0-9]*';
const surface = '(?:0|[1-9][0-9]*)';
const number = '[+-]?(?:[0-9]+(?:\\.[0-9]*)?|\\.[0-9]+)(?:[eE][+-]?[0-9]+)?';
const yesNo = '(?:YES|NO)';
const color = '(?:DEFAULT|BLA|RED|GRE|BLU|MAG|YEL|CYA|WHI|GRA|CHA|PIN|RUS|CHI|FOR|BAB|ROY)';
const diagnostic = `(?: S${surface}(?:\\.\\.${surface})?)?(?: Z${index})?`;
// A transport vocabulary, not analysis recipes. Only complete forms currently
// emitted by the service are admitted. References: CODE V 2026 Update 0.1,
// API pp37,39,46-48,51-56,78-79; Prompting pp15,30,58,131-135,145,147-148,151-153,155,186;

// API pp37,39,46-48,51-56,78-79; Prompting pp15,30,58,131-135,145,147-148,151,155,176,186;
// Macro-PLUS pp70,101-106,182,186,194,198; installed FL/NODP/FLPLOT macros.
const commands = [
  'LSF',
  // LSF: PromptingGuide p154. No filenames or free-form script fragments.
  `PIX ${number}`, `PLO LIS ${yesNo}`, `LAB ${yesNo}`, "TIT '[^'\";\\r\\n]{0,40}'",
  `ADD F${index}(?: W${index})?(?: [BQ])?`,
  `Y ${index} ${number}(?: ${number})*`, `YC ${number}(?: ${number})*`,
  'WHF\\?',
  // FAB construction/spacer tables: PromptingGuide p175. Index tables stay disabled.
  'FAB', 'IND NO', 'SUR YES', `SPA ${yesNo}`,
  // COS: PromptingGuide p175; native unit-dependent defaults remain omitted.
  'COS', `(?:BLD|SEP|EDG|DTH) ${number}`, `MAX [+-]?(?:0|${index})`,
  'FOO',
  // FOO: PromptingGuide pp141–142. NRD accepts fractional values natively.
  `SUR S${surface}`, `ORI S${surface} ${number} ${number}`,
  `BOU ${yesNo}`, `NRD ${number}`, `AZI ${number} ${number} ${number}`,
  `RAR ${number}`, `CLE (?:${yesNo}|${number})`, `(?:OBS|SKI) ${yesNo}`,
  'FIR', 'MTF', 'FIE', 'SPO', 'WAV', 'RAD', 'GDE', 'GLS', 'TRA', 'PSF', 'QUA', 'GO', 'ROF',

  'FIR', 'MTF', 'FIE', 'SPO', 'WAV', 'RAD', 'GDE', 'GLS', 'TRA', 'WEI', 'GO', 'ROF',

  `GAU(?: Z${index})? (?:NO|${number})`,
  `(?:FFO|IFO) ${number}`, 'NFO (?:[1-9]|1[0-8])',
  'FIR', 'MTF', 'FIE', 'GLS', 'RAD', 'GDE', 'TRA', 'GO', 'ROF',

  // SPE: PromptingGuide p185; HIDD_SPEPAN1/2. Replacement operations stay disabled.
  'SPE', '(?:RWL|RWT) NO', 'NWL (?:[1-9]|1[0-9]|2[01])',
  `BLA ${number}`, `RSP (?:${CODEV_SPECTRAL_RESPONSES.join('|')})`,
  `(?:FIO|THO)${diagnostic}`,
  `GLO (?:NO|S${surface} ${number} ${number} ${number})`,
  // ROF output items (PromptingGuide pp131-132): the ray fan lists only X and Y, in the format
  // the preceding bare ROF reset to (F11.5); flags, failure lines and the OPD line are unchanged.
  'ROF X Y',
  `RSI${diagnostic}(?: W${index})? (?:F${index} R[1-5]|F${index} ${number} ${number}(?: ${surface})?|${number} ${number} ${number} ${number}(?: ${surface})?)`,
  `(?:FFO|IFO)(?: Z${index})? ${number}`, `NFO(?: Z${index})? (?:[1-9]|1[0-8])`,
  `(?:MFR|IFR) ${number}`, `NRD(?: Z${index})? [+-]?(?:0|${index})`,
  `GAU(?: Z${index})? (?:NO|${number})`,
  `STE(?: Z${index})? ${number}`, `QRE(?: Z${index})? (?:NO|${number})`,
  `PER(?: Z${index})? ${number}`,
  `(?:XSC|YSC|COO|PLO|COM)(?: Z${index})? ${yesNo}`,
  `XSC(?: Z${index})? ${number}`,
  `(?:TGR|PGR) [+-]?(?:0|${index})`, `PRO ${yesNo}`,
  `ENC(?: Z${index})?(?: (?:CHI|CEN))? ${yesNo}`,
  `CHT (?:ALL|ENC|DET) ${yesNo}`, 'CHR NO', 'WGF NO',
  `FUL(?: Z${index})? ${yesNo}`, `PLI(?: Z${index})?(?: (?:GEO|STP))? ${yesNo}`,
  `(?:XMA|XDE|GRI)(?: Z${index})? ${number}`,
  `(?:DEX|DEY)(?: Z${index})? (?:NO|${number})`,
  `(?:GEO|WFR|DIA|SQW|PHA|DCS|DIO|ZFO|LIN|FFD) ${yesNo}`,
  `LIS(?: Z${index})? ${yesNo}`,
  'PAT (?:GRD|SOB|CIR)',
  `RTL (?:NRD ${index}|${number})`,
  `WVL W${index} ${yesNo}`,
  `(?:BES|NOM|THR) ${yesNo}`,
  'RFO NO',
  `WTF F${index}(?: Z${index})? ${number}`,
  `CPL F${index}(?: Z${index})? [+-]?(?:0|${index})`,
  `LSA (?:${yesNo}|${number})`,
  `(?:AST|DST) (?:CHR(?: ${number})?|${number})`,
  'IN CV_MACRO:FL',
  `IN CV_MACRO:SETVIG ${number} ${number} ${number} ${yesNo} ${yesNo}`,
  `IN CV_MACRO:NODP -?${surface} -?${surface}`,
  `IN CV_MACRO:FLPLOT ${surface} ${yesNo}`,
  `IN CV_MACRO:CHROMATICFOCALSHIFT Z(?:A|${index}) ${yesNo}`,
  `IN CV_MACRO:LATCOLOR ${surface} ${yesNo} ${color} ${color}`,
];
const expressions = [
  // Macro-PLUS p199: native per-surface contributions and SI system sums.
  // Keep surface/zoom explicit; no ranges, wavelength overrides or arithmetic.
  `\\((?:SA|TCO|TAS|SAS|PTB|DST|AX|LAT|PTZ) S(?:${index}|I) Z${index}\\)`,
  // Macro-PLUS pp91/94: fixed native scalar functions, no caller expressions.
  `SAGF\\(${surface},${surface},${number},${number}\\)`,
  `RMSSPOT\\(${index},${index},${index},${number}\\)`,
  // ZERNIKE: Macro-PLUS pp101–103.
  `ZERNIKE\\(${index},${index},${index},(?:[1-9]|[12][0-9]|3[0-7]),${surface},(?:0|(?:[1-9]|[12][0-9]|3[0-7])),'(?:ENP|EXP|EXS)',[0-5],'(?:PHASE|INTENSITY|PMA)','ZFR'\\)`,
  `ZERNIKE\\(${index},${index},${index},${index},${surface},${surface},'(?:ENP|EXP|EXS)',[0-5],'(?:PHASE|INTENSITY|PMA)','ZRN'\\)`,
  // ZERNIKEGQ: Macro-PLUS pp103–105.
  `ZERNIKEGQ\\(${index},${index},${index},(?:[1-9]|[12][0-9]|3[0-7]),(?:0|(?:[1-9]|[12][0-9]|3[0-7])),'(?:ENP|EXP|EXS)',[0-5],'(?:PHASE|INTENSITY)','ZFR'\\)`,
  `ZERNIKEGQ\\(${index},${index},${index},${index},${surface},'(?:ENP|EXP|EXS)',[0-5],'(?:PHASE|INTENSITY)','ZRN'\\)`,
  // ZFRCOEF/FITERROR: Macro-PLUS pp70,105–106.
  `ZFRCOEF\\(${index},${index},${index},(?:[1-9]|[12][0-9]|3[0-7]),${surface},(?:0|[1-9]|[12][0-9]|3[0-7]),'(?:ENP|EXP|EXS)'\\)`,
  `FITERROR\\(${index},${index},${index},0,'PHASE','ZFR'\\)`,
  '\\(SYM\\)', '\\(TYP FLD\\)', '\\(NFO\\)', '\\(ADM\\)',
  // FOC is the focal-surface number (Macro-PLUS p186), not a focus displacement.
  `\\((?:EFL|REF|POS|NFO|AFC|FOC|FFO|IFO) Z${index}\\)`,
  `\\(WL W${index}\\)`, `\\(WTW Z${index} W${index}\\)`,
  `\\((?:XAN|YAN|XOB|YOB|XIM|YIM|XRI|YRI) Z${index} F${index}\\)`,
  // Macro-PLUS p182: native directional pupil-clipping fractions.
  `\\((?:VUX|VLX|VUY|VLY) Z${index} F${index}\\)`,

  // K: the conic a CODE V PIK K writes (#7699); the export gives both pickup ends a
  // K (CON K 0 for a sphere), since EVA (K Sk) errors on a sphere (Macro-PLUS p188).
  `\\((?:RDY|THI|K) S${surface} Z${index}\\)`,
  // Cross-section layout dataset. SD with MEC and no overage arguments is MAV
  // (Opt_5_Constraints.06.12; element.13.04): the maximum clear-aperture extent
  // from the vertex, or the default aperture when none is defined. XSC, YSC, ZSC
  // (vertex) and ASC, BSC, CSC (Euler angles, degrees) are the GSC items of
  // surface k relative to global reference surface j (DatabaseRef.4.10;
  // HIDD_GLOBAL_COORDINATES). Zn is omitted: the drawing covers zoom 1 only and
  // the corpus has no multi-zoom lens, so the zoomed forms are unprobed.
  `\\(SD S${surface} MEC\\)`,
  `\\((?:XSC|YSC|ZSC|ASC|BSC|CSC) S${surface} G${index}\\)`,
  // Native materials, refract mode, surface type and apertures (DatabaseRef.4.07;
  // probes p4/p5 2026-10-29, GROUNDING "Cross-section layout dataset"). GLA is
  // the catalog token, "" for air; p4's REFL surface (in air) read "", so RMD
  // ('REFL'|'REFR'|'TIRO'|'TIR') tells a mirror apart; TYP SUR is the surface
  // type string; NUM APE counts the apertures of a type; ASH is aperture An's
  // shape (CIG|ELG|REG) and APE its half-widths (SZX|SZY), decenters (ADX|ADY)
  // and rotation (ARO).
  `\\((?:GLA|RMD) S${surface}\\)`,
  `\\(TYP SUR S${surface}\\)`,
  `\\(NUM APE S${surface} (?:CLR|OBS)\\)`,
  `\\(ASH S${surface} A${index} (?:CLR|OBS)\\)`,
  `\\(ORA S${surface} A${index} (?:CLR|OBS)\\)`,
  `\\(APE S${surface} A${index} (?:CLR|OBS) (?:SZX|SZY|ADX|ADY|ARO)\\)`,
  `\\(\\(WTW Z${index} W${index}\\) <> 0\\)`,
  `\\(\\((?:YAN|XAN|YOB|XOB|YIM|XIM|YRI|XRI) FL Z${index}\\) <> 0\\)`,
  `\\(\\(X R1 FL Z${index} SI\\) <> 0\\)`,
];
const properties = ['CodeVVersion', 'Dimension', 'MaxTextBufferSize', 'SurfaceCount', 'StopSurface', 'ZoomCount', 'WavelengthCount', 'FieldCount'] as const;
// The line starts that mean a CODE V command, command file (`IN`) or macro
// failed. Both Python consumers (worker `session.py`, service `native/text.py`)
// build their detectors from this list through the generated grammar; nothing
// else defines a native failure. Column-0 `Error:` is the only spelling in the
// captures (grounding ledger row 31; 13,658 captured outputs scanned
// 2026-10-23). `ERROR - ` is the first-line format Macro-PLUS p150 documents
// for CVERROR (the example on the same page prints `Error:`; both are admitted).
// `Warning: Sequence aborted` is CODE V halting a command file or macro after
// an error (Macro-PLUS p150; PromptingGuide p28, NAB; ledger row 46): the lens
// is partially applied whether or not the preceding error line was recognized.
// RELILLUM sample_points=1 emits this exact four-space arithmetic-fault prefix.
export const CODEV_NATIVE_FAILURE_LINES = ['Error:', 'ERROR - ', '    ERROR - ', 'Warning: Sequence aborted'] as const;
// A listing sent to an OUT file (only AUT does) also writes a failure where the
// terminal prints `Error:` with five spaces: "     ERROR - No improvement
// possible - The entire system is frozen" (test worker, 2026-11-01).
export const CODEV_OUT_FILE_FAILURE_LINES = [...CODEV_NATIVE_FAILURE_LINES, '     ERROR - '] as const;
// JSON Schema has no RegExp flags; encode case in the pattern itself so the
// Python service and TypeScript accept the same echoed operation strings.
function full(patterns: readonly string[]): RegExp {
  let inClass = false;
  const pattern = [...patterns.join('|')].map(char => {
    if (char === '[') inClass = true;
    if (char === ']') inClass = false;
    return !inClass && /[A-Z]/.test(char) ? `[${char}${char.toLowerCase()}]` : char;
  }).join('');
  return new RegExp(`^(?:${pattern})(?![\\s\\S])`);
}
const CommandOperation = z.strictObject({ kind: z.literal('command'), command: z.string().regex(full(commands)) });
const PropertyOperation = z.strictObject({ kind: z.literal('property'), name: z.enum(properties) });
const ExpressionOperation = z.strictObject({ kind: z.literal('expression'), expression: z.string().regex(full(expressions)) });
const ApertureOperation = z.strictObject({ kind: z.literal('max_aperture'), surface: z.number().int().min(0).max(2147483647), zoom: z.number().int().min(1).max(2147483647) });
// APIReferenceGuide 2026 Update 0.1 pp78–79. No defaults: zeros are explicit
// native sentinels. Native CODE V validates lens-dependent and grid constraints.
const positiveLong = z.number().int().min(1).max(2147483647);
const nonnegativeLong = z.number().int().min(0).max(2147483647);
// Macro-PLUS pp96–97: one ten-element native array; 0 selects native RTL 0.01.
const SpotDataOperation = z.strictObject({
  kind: z.literal('spot_data'), zoom: positiveLong, field: positiveLong, focus: positiveLong,
  sampling: z.union([z.number().min(0).lt(1), z.number().gt(1)]),
  reference: z.enum(['DEF', 'CRA', 'CEN', 'MSC', 'USR']), x_shift: z.number(), y_shift: z.number(),
});
// Installed WaveAberrationTable/Define: Macro-PLUS pp325–326,389–391.
const WaveAberrationsOperation = z.strictObject({
  kind: z.literal('wave_aberrations'), zoom: positiveLong,
  output_flag: z.number().int().min(0).max(5),
});
const PsfDataOperation = z.strictObject({
  kind: z.literal('psf_data_1fld'),
  zoom: positiveLong, field: positiveLong, wavelength: nonnegativeLong, focus: positiveLong,
  tgr: nonnegativeLong, pgr: nonnegativeLong, nrd: nonnegativeLong,
  gri: z.number(), pro: z.union([z.literal(0), z.literal(1)]), xsc: z.number(),
  pha: z.union([z.literal(0), z.literal(1)]),
  norm: z.union([z.literal(-1), z.literal(-2), z.literal(-3)]), thr: z.number(),
});
// API pp62–63,78–79: fixed native PSF -> square enclosure, under one seat.
// The intensity grid is native output, never accepted from the caller.
export const CodevPsfEnclosedEnergyOperationSchema = PsfDataOperation.extend({
  kind: z.literal('psf_enclosed_energy'),
  xsc: z.literal(0), pha: z.literal(0), norm: z.literal(-1), thr: z.literal(0),
  center: z.number().int().min(0).max(5),
  x_offset: z.number(), y_offset: z.number(),
  percentages: z.array(z.number().min(0).max(100)).min(1),
});
export { PsfDataOperation as CodevPsfDataOperationSchema };
// Option-local WHF + GO, with a worker-owned temporary destination. The worker
// returns unchanged export bytes; callers cannot select paths or scripts.
const ChartHdf5Operation = z.strictObject({ kind: z.literal('chart_hdf5') });

// APIReferenceGuide 2026 Update 0.1 pp87–88. RMSWE computes all fields;
// zero wavelength means polychromatic and zero NRD delegates sampling to CODE V.
export const CodevRmsweOperationSchema = z.strictObject({
  kind: z.literal('rmswe'),
  zoom: positiveLong, wavelength: nonnegativeLong, nrd: nonnegativeLong,
  focus: z.union([z.literal(0), z.literal(1)]),
});
// Installed Example_CVApplication.py/.vb/.m and native build43679075 require
// six slots. Preserve the five documented WRX/WRY/RCX/RCY/AZI inputs and the
// installed examples' final zero. Actual typelib enum18 is PANG, not PDF MSD1.
export const CodevGaussbeamOperationSchema = z.strictObject({
  kind: z.literal('gaussbeam'),
  surface: nonnegativeLong, zoom: positiveLong, field: positiveLong, wavelength: positiveLong,
  input: z.tuple([z.number(), z.number(), z.number(), z.number(), z.number(), z.literal(0)]).meta({ minItems: 6, maxItems: 6 }),
  output: z.number().int().min(0).max(18),
});
// The service owns the option recipe; the worker owns a scratch worksheet for
// the whole call. Transport support does not register a field-map analysis.
// PromptingGuide pp136–140; API p103; Macro-PLUS pp424–435.
const worksheetCommands = [
  `SLC (?:MTF|GMT) ${number}(?: ${number})?`,
  'SLC (?:RWE|RSD|AST|DST|CTD|WPO)',
  'FFD (?:RSD|DST|CTD|WPO)',
  `WCN (?:ONL )?${index}`,
  `WPT (?:(?:ZRN|ZFR)(?: ${index})?|${index})`,
  'PCS (?:ENP|EXP|EXS)', `TGR [+-]?(?:0|${index})`,
  `FFD (?:MTF|GMT) ${number}(?: ${number})?`,
  `NRD [+-]?(?:0|${index})`, `LIS ${yesNo}`,
  `FCO (?:(?:OBJ|IMG|RIH|ANG|AZE)(?: '[A-Za-z0-9 .,()/_:+%-]{1,27}')?|'[A-Za-z0-9 .,()/_:+%-]{1,27}')`,
  `FCO CIR(?: (?:OBJ|IMG|RIH|ANG|AZE)(?: '[A-Za-z0-9 .,()/_:+%-]{1,27}')?)?`,
  `(?:XMI|XMA|YMI|YMA)(?: Z${index})? ${number}`,
  `(?:NFX|NFY)(?: Z${index})? [+-]?(?:0|${index})`,
];
const pupilMapCommands = [
  'PLO NO', `LIS ${yesNo}`, `TGR [+-]?(?:0|${index})`,
  `PIN (?:POL )?(?:YES|${number})`,
  `(?:NRD|GRI|XSC) ${number}`,
  `ADW (?:(?:TIL|FOC)(?: ${number} ${number} ${number} ${number})?|${number} ${number} ${number} ${number})`,
];
export const CodevWorksheetOperationSchema = z.discriminatedUnion('option', [
  z.strictObject({
    kind: z.literal('worksheet'), option: z.literal('FMA'),
    commands: z.array(z.string().regex(full(worksheetCommands))).min(1),
  }),
  z.strictObject({
    kind: z.literal('worksheet'), option: z.literal('PMA'),
    commands: z.array(z.string().regex(full(pupilMapCommands))).min(1),
  }),
]);
// Installed RELILLUM.SEQ, Macro-PLUS pp307/412. Fixed WBF 0 and empty
// native template; no caller paths, worksheet selection or generated commands.
const RelativeIlluminationOperation = z.strictObject({
  kind: z.literal('relative_illumination'),
  sample_points: z.number().int().nonnegative(),
  rays_across_diameter: z.number().int().nonnegative(),
  pupil_overfill: z.number().finite().positive(),
});
// SDERIVF: API p91; Macro-PLUS pp95–96. Every coordinate remains explicit.
const SurfaceDerivativesOperation = z.strictObject({
  kind: z.literal('sderivf'), surface: nonnegativeLong, zoom: positiveLong, x: z.number(), y: z.number(),
});
// SASF: API p90; Macro-PLUS p95. Native build 43679075 accepts zoom 0.
const SurfaceCurvaturesOperation = z.strictObject({
  kind: z.literal('sasf'), surface: nonnegativeLong, zoom: nonnegativeLong, x: z.number(), y: z.number(),
});
// Installed FIRABCD macro: Macro-PLUS pp309–314. No caller code or path.
const FirAbcdOperation = z.strictObject({ kind: z.literal('fir_abcd'), field: positiveLong });
// Installed YYBAR.SEQ: both string flags are explicit; no caller macro names or code.
const YybarOperation = z.strictObject({
  kind: z.literal('yybar'), paraxial: z.enum(['YES', 'NO']), real: z.enum(['YES', 'NO']),
});
// APIReferenceGuide 2026 Update 0.1 pp73–74; Macro-PLUS pp77–78.
// All eight native arguments are explicit; CODE V validates lens-dependent inputs.
export const CodevMtfOperationSchema = z.strictObject({
  kind: z.literal('mtf_1fld'),
  zoom: positiveLong, field: positiveLong, frequency: z.number(), azimuth: z.number(),
  nrd: nonnegativeLong,
  mtf_type: z.union([z.literal(0), z.literal(1)]),
  mtf_wave: z.union([z.literal(0), z.literal(1)]),
  through_focus: z.union([z.literal(0), z.literal(1)]),
});
// One RSI ray. The worker returns its listing and any native failure line (a
// missed surface, total reflection) as data, so a fan of rays survives one
// untraceable ray; probed 2026-10-29: CODE V stays in command mode after the
// miss. Diagnostic rsi.03.05: `RSI [Sk | Si..j] [Zk] [Wn] Fm x_fract_pupil
// y_fract_pupil`; W omitted traces the reference wavelength (rsi.03.06), written
// here as an explicit null. Deliberately absent from CODEV_BATCH_DIAGNOSTICS
// below: the failure line is the returned value (value.failure), not a batch
// error for the service to scan for.
const RsiRayOperation = z.strictObject({
  kind: z.literal('rsi_ray'), field: positiveLong, wavelength: positiveLong.nullable(),
  pupil_x: z.number(), pupil_y: z.number(),
});

// A fan of RSI rays traced inside one operation, in the rsi.03.05 forms with the F
// qualifier: the chief ray `R1` (rsi.03.06) and then every pupil point, once per
// wavelength (every wavelength when `wavelength` is null; W is always written), for
// one zoom position or all of them (Z omitted, rsi.03.06). The worker writes those
// commands into one sequence file of its own, runs it with `IN` with the listing
// directed to a worker-owned file (`OUT filespec`, PromptingGuide p27), restores
// `OUT T` and returns that file verbatim: one native call per fan instead of one per
// ray, the same listing per ray (probe of 2026-11-01, GROUNDING "Ray fans traced
// from one sequence file"). A ray's failure line is listing data, like rsi_ray's;
// the `commands` channel below holds only the replies to `IN` and `OUT T`.
export const RSI_FAN_MAX_RAYS = 400;
const RsiFanOperation = z.strictObject({
  kind: z.literal('rsi_fan'), field: positiveLong, zoom: positiveLong.nullable(), wavelength: positiveLong.nullable(),
  pupil: z.array(z.tuple([z.number(), z.number()]).meta({ minItems: 2, maxItems: 2 })).min(1).max(RSI_FAN_MAX_RAYS),
});

// A synchronous AUT run answers inside the tunnel's 300 s response cut (#7858)
// together with the lens load and WRL readback; the worker stops GO at
// max_seconds or on /aut-cancel (StopCommand, then N) and keeps the values
// reached. run_id names the run for /aut-progress and /aut-cancel, which any
// worker process can answer while the run holds the seat.
const AUT_MAX_SECONDS = 240;
export const AUT_LINE = full(autLines);
const AutOperation = z.strictObject({
  kind: z.literal('aut'), run_id: z.string().regex(/^[0-9a-f]{32}$/), lines: z.array(z.string().regex(AUT_LINE)),
  max_cycles: positiveLong, max_seconds: z.number().int().min(1).max(AUT_MAX_SECONDS),
});

export const CodevBatchOperationSchema = z.discriminatedUnion('kind', [CommandOperation, PropertyOperation, ExpressionOperation, ApertureOperation, PsfDataOperation, CodevPsfEnclosedEnergyOperationSchema, ChartHdf5Operation, CodevGaussbeamOperationSchema, CodevWorksheetOperationSchema, SpotDataOperation, SurfaceDerivativesOperation, SurfaceCurvaturesOperation, CodevRmsweOperationSchema, CodevMtfOperationSchema, WaveAberrationsOperation, RelativeIlluminationOperation, FirAbcdOperation, YybarOperation, RsiRayOperation, RsiFanOperation, AutOperation]);
// Operations whose raw `value` carries native diagnostic text, listed by kind
// with the channels in native order. `commands` and `cleanup` are lists of
// `{output}` records, `array_diagnostic` a string, and `output` the result's
// own output field consumed at that point in the order (RELILLUM prints its
// macro output between the setup commands and the cleanup). The service reads
// these channels for native errors and warnings, so a new structured operation
// declares its channels here, next to its schema, instead of in service code.
export const CODEV_BATCH_DIAGNOSTICS = {
  psf_enclosed_energy: { name: 'PSF enclosed energy', channels: ['commands', 'output'] },
  spot_data: { name: 'spot data', channels: ['commands', 'array_diagnostic', 'cleanup'] },
  fir_abcd: { name: 'FIRABCD', channels: ['commands', 'cleanup'] },
  yybar: { name: 'YYBAR', channels: ['commands', 'cleanup'] },
  worksheet: { name: 'worksheet', channels: ['commands', 'array_diagnostic', 'cleanup'] },
  wave_aberrations: { name: 'wave aberrations', channels: ['commands', 'array_diagnostic', 'cleanup'] },
  relative_illumination: { name: 'relative illumination', channels: ['commands', 'cleanup', 'output'] },
  rsi_fan: { name: 'ray fan', channels: ['commands'] },
  aut: { name: 'AUT', channels: ['commands', 'cleanup'] },
} as const satisfies Partial<Record<CodevBatchOperation['kind'], { name: string; channels: readonly ('commands' | 'array_diagnostic' | 'cleanup' | 'output')[] }>>;
export const CODEV_BATCH_GRAMMAR = {
  commands, expressions, properties, worksheet_commands: worksheetCommands, pupil_map_commands: pupilMapCommands,
  aut_lines: autLines,
  operations: CodevBatchOperationSchema.options.map(operation =>
    (operation instanceof z.ZodDiscriminatedUnion ? operation.options[0] : operation).shape.kind.value),
  diagnostics: CODEV_BATCH_DIAGNOSTICS,
  failure_lines: CODEV_NATIVE_FAILURE_LINES,
  out_file_failure_lines: CODEV_OUT_FILE_FAILURE_LINES,
} as const;
export const CodevBatchRequestSchema = z.strictObject({
  transport_version: z.literal(CODEV_BATCH_VERSION), seq_content: z.string().min(1), operations: z.array(CodevBatchOperationSchema),
});
const InputLoad = z.strictObject({ sha256: z.string().regex(/^[a-f0-9]{64}$/), load_output: z.string() });
const Result = z.discriminatedUnion('status', [
  z.strictObject({ operation: CodevBatchOperationSchema, status: z.literal('ok'), value: z.json(), output: z.json().optional() }),
  z.strictObject({ operation: CodevBatchOperationSchema, status: z.literal('error'), value: z.json().optional(), output: z.json().optional(), error: z.string().min(1) }),
]);
const envelope = {
  transport_version: z.literal(CODEV_BATCH_VERSION), worker_version: z.string().min(1), native_version: z.string().min(1).nullable(), results: z.array(Result),
  /** SHA-256 of the worker's batch_grammar.json (CR stripped): which grammar served this batch. Evidence, not a
   * gate: workers deploy before the service, and the worker refuses (422) any operation outside its own grammar.
   * The service logs a differing hash once. Optional until every deployed worker reports it. */
  grammar_sha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
};
export const CodevBatchResponseSchema = z.discriminatedUnion('success', [
  z.strictObject({ ...envelope, success: z.literal(true), input_seq: InputLoad, rejected_input_seq: z.null(), error: z.null() }),
  z.strictObject({ ...envelope, success: z.literal(false), input_seq: InputLoad.nullable(), rejected_input_seq: InputLoad.nullable(), error: z.string().min(1) }),
]);
export type CodevBatchOperation = z.infer<typeof CodevBatchOperationSchema>;
export type CodevBatchRequest = z.infer<typeof CodevBatchRequestSchema>;
export type CodevBatchResponse = z.infer<typeof CodevBatchResponseSchema>;
